import {
  BusinessCenter as NoOfficesIcon,
  Add as AddIcon,
  Business as BusinessIcon
} from '@mui/icons-material'
import { Avatar, Box, Typography, Paper, Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'

import routes from '../../pages/routes'
import { OfficeWithRole } from '../../utils/profileHelpers'

import OfficeManagementCard from './OfficeManagementCard'

/**
 * Props for the OfficeManagementList component
 */
export interface OfficeManagementListProps {
  /** Array of offices with role information */
  offices: OfficeWithRole[]
  /** Loading state indicator */
  loading?: boolean
}

/**
 * OfficeManagementList - Office management list display component
 *
 * Displays all offices user has access to with management capabilities.
 * Shows edit, view, and desk management options for each office.
 * Handles empty state when user has no office access.
 */
const OfficeManagementList = ({
  offices,
  loading = false
}: OfficeManagementListProps) => {
  const navigate = useNavigate()

  // Handle navigation to office creation page
  const handleCreateOffice = () => {
    navigate(routes.createOffice)
  }

  // Show loading state
  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            <BusinessIcon fontSize='small' />
          </Avatar>
          <Typography variant='h6' component='h2' sx={{ fontWeight: 600 }}>
            Your Offices
          </Typography>
        </Box>
        <Typography variant='body2' color='text.secondary'>
          Loading your offices...
        </Typography>
      </Box>
    )
  }

  // Show empty state when user has no office access
  if (!offices || offices.length === 0) {
    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <BusinessIcon fontSize='small' />
            </Avatar>
            <Typography variant='h6' component='h2' sx={{ fontWeight: 600 }}>
              Your Offices
            </Typography>
          </Box>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleCreateOffice}
            sx={{ ml: 2 }}
          >
            Create Office
          </Button>
        </Box>
        <Paper
          variant='outlined'
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: 'background.default'
          }}
        >
          <NoOfficesIcon
            sx={{
              fontSize: 48,
              color: 'text.secondary',
              mb: 2
            }}
          />
          <Typography variant='body1' color='text.secondary' gutterBottom>
            No offices found
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            You don't have access to any offices yet. Create your own office or
            contact an office administrator to get invited.
          </Typography>
          <Button
            variant='outlined'
            size='large'
            startIcon={<AddIcon />}
            onClick={handleCreateOffice}
          >
            Create Your First Office
          </Button>
        </Paper>
      </Box>
    )
  }

  // Display offices with management capabilities
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            <BusinessIcon fontSize='small' />
          </Avatar>
          <Typography variant='h6' component='h2' sx={{ fontWeight: 600 }}>
            Your Offices
          </Typography>
        </Box>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={handleCreateOffice}
          sx={{ ml: 2 }}
        >
          Create Office
        </Button>
      </Box>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        You have access to {offices.length} office
        {offices.length !== 1 ? 's' : ''}. Click on an office to view details or
        manage desks.
      </Typography>

      <Box>
        {offices.map(office => (
          <OfficeManagementCard
            key={office.oid || `office-${Math.random()}`}
            office={office}
          />
        ))}
      </Box>
    </Box>
  )
}

export default OfficeManagementList
